'use client'

import { useQueryClient } from '@tanstack/react-query'
import { FlaskConical } from 'lucide-react'
import { memo } from 'react'

import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  SingleSelect,
  Switch,
} from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { MOCK_ENABLED } from '@lib/constants'
import { dayjs, getAppTimezone } from '@lib/dayjs'
import { DEFAULT_MOCK_CONFIG, MOCK_DELAY_MAX, useMockMode, type MockConfig } from '@lib/mockMode'
import { DateSinglePicker } from '@ui/DateSinglePicker'

// Force a specific HTTP status so the UI's handling of each outcome can be exercised on demand.
const CODE_OPTIONS = [
  { code: 200, hint: 'OK — normal success response' },
  { code: 400, hint: 'Bad request — malformed params' },
  { code: 401, hint: 'Unauthorized — session expired / login redirect' },
  { code: 403, hint: 'Forbidden — missing permission' },
  { code: 404, hint: 'Not found — missing resource / session re-check' },
  { code: 422, hint: 'Unprocessable — validation errors' },
  { code: 429, hint: 'Too many requests — rate limited' },
  { code: 500, hint: 'Server error — generic failure toast' },
].map(({ code, hint }) => ({ label: `${code} — ${hint}`, value: String(code) }))

type InputField = { key: 'count' | 'seed' | 'delay'; label: string; type: string; placeholder?: string; max?: number }

const INPUT_FIELDS: InputField[] = [
  { key: 'count', label: 'Count', type: 'number', placeholder: 'Default' },
  { key: 'seed', label: 'Seed', type: 'text', placeholder: 'Reproducible key' },
  { key: 'delay', label: `Delay ms (≤${MOCK_DELAY_MAX})`, type: 'number', max: MOCK_DELAY_MAX },
]

const toNum = (raw: string): number | null => {
  const n = Number(raw)

  return raw.trim() && Number.isFinite(n) ? n : null
}

export const MockModeControl = memo(() => {
  const { config, setConfig } = useMockMode()
  const qc = useQueryClient()

  if (!MOCK_ENABLED) return null

  const update = (partial: Partial<MockConfig>) => {
    setConfig(partial)
    qc.invalidateQueries()
  }

  const active = config.enabled

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-pressed={active}
          aria-label='Mock mode settings'
          title='Mock mode'
          className={cn(
            'fixed right-0 top-0 z-50 size-6 rounded-md rounded-tr-none bg-card/40 backdrop-blur-sm transition-opacity',
            'hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
            active ? 'bg-warning/15 text-warning opacity-100 hover:!text-warning' : 'text-muted-foreground opacity-0',
          )}
        >
          <FlaskConical className={cn(active && 'fill-current')} />
        </Button>
      </PopoverTrigger>

      <PopoverContent align='end' sideOffset={6} className='flex w-72 flex-col gap-3'>
        <div className='flex items-center justify-between'>
          <span className='flex items-center gap-1.5 text-sm font-medium text-foreground'>
            <FlaskConical className='size-3.5' />
            Mock mode
          </span>
          <Switch checked={active} onCheckedChange={(v) => update({ enabled: v })} aria-label='Toggle mock mode' />
        </div>

        <p className='text-xs text-muted-foreground'>
          Applied only to <code className='rounded bg-muted px-1 py-0.5'>risk_engine</code> requests.
        </p>

        <Separator />

        <div className={cn('flex flex-col gap-3', !active && 'pointer-events-none opacity-50')}>
          <div className='flex items-center justify-between'>
            <span className='flex items-center gap-1 text-xs text-muted-foreground'>
              <span>Force Prism</span>
            </span>
            <Switch
              size='sm'
              checked={config.forcePrism}
              disabled={!active}
              onCheckedChange={(v) => update({ forcePrism: v })}
              aria-label='Force Prism'
            />
          </div>

          {INPUT_FIELDS.map(({ key, label, type, placeholder, max }) => (
            <div key={key} className='flex flex-col gap-1.5'>
              <Label htmlFor={`mock-${key}`} className='text-xs text-muted-foreground'>
                {label}
              </Label>
              <Input
                id={`mock-${key}`}
                type={type}
                min={type === 'number' ? 0 : undefined}
                max={max}
                placeholder={placeholder}
                disabled={!active}
                value={config[key] ?? ''}
                onChange={(e) =>
                  update({ [key]: type === 'number' ? toNum(e.target.value) : e.target.value.trim() || null })
                }
              />
            </div>
          ))}

          <div className='flex flex-col gap-1.5'>
            <span className='text-xs text-muted-foreground'>Now (date)</span>
            <DateSinglePicker
              isFullWidth
              showTime={false}
              disabled={!active}
              value={config.now}
              onChange={(iso) => update({ now: iso ? dayjs.utc(iso).tz(getAppTimezone()).format('YYYY-MM-DD') : null })}
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <span className='text-xs text-muted-foreground'>Force status</span>
            <SingleSelect
              placeholder='Default'
              disabled={!active}
              options={CODE_OPTIONS}
              value={config.code != null ? String(config.code) : null}
              onChange={(v) => update({ code: v ? Number(v) : null })}
            />
          </div>
        </div>

        <div className='flex items-center justify-between'>
          <Button variant='ghost' size='sm' onClick={() => update(DEFAULT_MOCK_CONFIG)}>
            Reset
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
})

MockModeControl.displayName = 'MockModeControl'
